import React, { useState, useRef, useMemo, Fragment } from "react";
import MedicineSelector from "./MedicineSelector";
import InputCounter from "./InputCounter";
import InputSelectList from "./InputSelectList";
import InputCheckBoxes from "./InputCheckBoxes";
import InputTextarea from "./InputTextarea";
import AppModalAlertMessage from "./AppModalAlertMessage";
import getErrorMessage from "../utilities/getErrorMessage";
import axiosInstance from "../utilities/axiosInstance";

const QUANTITY_UNITS = ["Bottle", "Stripe", "Box", "Sachet"];
const DOSAGE_UNITS = ["ml", "mg", "drops", "capsules"];
const WHEN_TO_USE_OTHER = "Other";
const WHEN_TO_USES = ["Before meal", "With meal", "After meal", "Morning", "Afternoon", "Evening", WHEN_TO_USE_OTHER];

const formatToSavedWhenToUses = (whenToUses = []) => {
	const otherValues = WHEN_TO_USES.reduce((wtu, W) => wtu.filter((w) => W !== w), whenToUses);

	if (otherValues.length > 0) {
		whenToUses = whenToUses.filter((w) => w !== otherValues[0]).concat(WHEN_TO_USE_OTHER);
		return [whenToUses, otherValues[0]];
	}

	return [whenToUses, null];
};

const MedicineForm = ({ consultation_id, medicine }) => {
	const [savedWhenToUses, savedWhenToUseOther] = formatToSavedWhenToUses(medicine?.when_to_use);
	const [medication, setMedication] = useState({
		code: null,
		id: medicine?.medication_id || null,
		name: medicine?.name || null,
	});
	const [quantity, setQuantity] = useState(medicine?.quantity || 0);
	const [quantityUnit, setQuantityUnit] = useState(medicine?.quantity_unit || "");
	const [dosage, setDosage] = useState(medicine?.dosage || 0);
	const [dosageUnit, setDosageUnit] = useState(medicine?.dosage_unit || "");
	const [frequency, setFrequency] = useState(medicine?.frequency || 0);
	const [whenToUses, setWhenToUses] = useState(savedWhenToUses || []);
	const [whenToUseOther, setWhenToUseOther] = useState(savedWhenToUseOther || "");
	const [note, setNote] = useState(medicine?.note || "");
	const [alertMessage, setAlertMessage] = useState({ success: null, error: null });
	const isEdit = !!medicine;
	const submitButton = useRef();

	const [isFormError, formErrorMessage] = useMemo(() => {
		let err = [];
		if (medication?.id === null) {
			err.push("please choose medicine");
		} else if (quantity == 0) {
			err.push("quantity should greater than 0");
		} else if (dosage == 0) {
			err.push("dosage should greater than 0");
		} else if (frequency == 0) {
			err.push("times a day should greater than 0");
		} else if (quantityUnit === "") {
			err.push("quantity unit required");
		} else if (dosageUnit === "") {
			err.push("dosage unit required");
		} else if (whenToUses.length === 0) {
			err.push("when to use is required");
		}
		return [err.length > 0, err];
	}, [medication, quantity, quantityUnit, frequency, dosage, dosageUnit, whenToUses]);

	const assignWhenToUsesOther = () => whenToUses.map((wtu) => (wtu === WHEN_TO_USE_OTHER ? whenToUseOther : wtu));

	const saveMedicine = () =>
		new Promise(async (resolve, reject) => {
			try {
				const when_to_use = assignWhenToUsesOther();
				const request = {
					consultation_id,
					medicine: {
						medication_id: medication.id,
						quantity_unit: quantityUnit,
						dosage_unit: dosageUnit,
						when_to_use,
						note,
						quantity,
						dosage,
						frequency,
					},
				};
				const { data } = isEdit
					? await axiosInstance.put(`/api/doctor/medicines/${medicine.id}`, request)
					: await axiosInstance.post("/api/doctor/medicines", request);
				resolve(data);
			} catch (error) {
				reject(error);
			}
		});

	const resetAllFormValues = () => {
		setQuantity(0);
		setMedication({ code: null, id: null, name: null });
		setQuantityUnit("");
		setDosage(0);
		setDosageUnit("");
		setFrequency(0);
		setWhenToUses([]);
		setWhenToUseOther("");
		setNote("");
	};

	const setButtonDisabled = (isDisabled = true) => {
		const isHasDisabled = submitButton.current.hasAttribute("disabled");
		if (isDisabled && !isHasDisabled) {
			submitButton.current.setAttribute("disabled", "");
		} else if (!isDisabled && isHasDisabled) {
			submitButton.current.removeAttribute("disabled");
		}
	};

	const onSubmit = (e) => {
		e.preventDefault();
		setButtonDisabled();
		console.log("Consultation id is", consultation_id);
		if (isFormError) {
			return;
		}

		saveMedicine()
			.then((response) => {
				if (response.success) {
					setAlertMessage({ error: null, success: response.message });
				}

				if (!isEdit) {
					resetAllFormValues();
				} else {
					setTimeout(() => Turbolinks.visit(`/doctor/consultations/${consultation_id}`), 1500);
				}
			})
			.catch((err) => {
				const error = getErrorMessage(err);
				setAlertMessage({ success: null, error });
			})
			.finally(() => setButtonDisabled(false));
	};

	return (
		<Fragment>
			<form onSubmit={onSubmit}>
				<MedicineSelector consultation_id={consultation_id} value={medication} setValue={setMedication} />
				<h5 className="mt-2 pd-l-r-16">Quantity :</h5>
				<div className="d-flex pd-l-r-16">
					<div className="col-5">
						<InputCounter value={quantity} setValue={setQuantity} />
					</div>
					<div className="col-7">
						<InputSelectList
							label="Quantity"
							defaultValueLabel="Bottle..."
							validFeedback="Looks good!"
							invalidFeedback="Please choose a Quantity unit."
							value={quantityUnit}
							setValue={setQuantityUnit}
							valueList={QUANTITY_UNITS.map((q) => ({ key: q, value: q }))}
						/>
					</div>
				</div>
				<div className="border_bt"></div>
				<h5 className="mt-2 pd-l-r-16">How to use :</h5>
				<div className="d-flex pd-l-r-16">
					<div className="col-5">
						<InputCounter value={dosage} setValue={setDosage} countBy={0.5} />
					</div>
					<div className="col-7">
						<InputSelectList
							label="How to use"
							defaultValueLabel="Dosage..."
							validFeedback="Looks good!"
							invalidFeedback="Please choose a How to use."
							value={dosageUnit}
							setValue={setDosageUnit}
							valueList={DOSAGE_UNITS.map((q) => ({ key: q, value: q }))}
						/>
					</div>
				</div>
				<div className="d-flex pd-l-r-16">
					<div className="col-5">
						<InputCounter value={frequency} setValue={setFrequency} />
					</div>
					<div className="col-7">
						<div className="form-group boxed">
							<div className="input-wrapper">
								<input
									className="form-control"
									disabled
									placeholder="times a day"
									type="text"
									style={{ background: "#eee" }}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="border_bt"></div>
				<h5 className="mt-2 pd-l-r-16">When to use :</h5>
				<div className="pd-l-r-16 mt-3">
					<InputCheckBoxes
						value={whenToUses}
						setValue={setWhenToUses}
						valueList={WHEN_TO_USES.map((q) => ({ key: q, value: q }))}
					/>
					{whenToUses.includes(WHEN_TO_USE_OTHER) && (
						<div className="row">
							<div className="col-12">
								<InputTextarea label="Other" value={whenToUseOther} setValue={setWhenToUseOther} />
							</div>
						</div>
					)}
				</div>
				<div className="border_bt"></div>
				<div className="pd-l-r-16 mb-5">
					<InputTextarea label="Note" value={note} setValue={setNote} />
				</div>
				<div className="position-fixed-bottom fixed-bg" style={{ left: 0, right: 0, zIndex: 9999991 }}>
					<button
						className="btn btn-primary btn-block"
						type="submit"
						ref={submitButton}
						disabled={isFormError}
					>
						Submit
					</button>
				</div>
			</form>
			<AppModalAlertMessage message={alertMessage} setMessage={setAlertMessage} />
		</Fragment>
	);
};

export default MedicineForm;
